import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    mergeMap,
    takeUntil,
} from 'rxjs/operators';

import {
    CommitmentEntity,
    ObservationEntity,
} from '../../coaching-base/config';
import {
    CommitmentSummaryListItem,
    ObservationSummaryDto,
} from '../../coaching-base/general';
import { CommitmentService } from '../../coaching-commitment/services/commitment.service';
import { ObservationService } from '../../coaching-observation/observation/observation.service';
import { Dependencies } from '../../store';
import { PayloadAction } from '../../store.model';

import { ActionType, Reload, reloadResult } from './pillar-two.action';
import { TabRefId } from './tab-ref-id.model';

type ForkRet = [
    Observable<CommitmentSummaryListItem[]>,
    Observable<CommitmentEntity>,
    Observable<ObservationSummaryDto[]>,
    Observable<ObservationEntity>,
];

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const reload = (
    action: ActionsObservable<PayloadAction<ActionType, Reload>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_DETAIL__P2__RELOAD),
        mergeMap((act) => {
            const commitmentService =
                deps.injector.get<CommitmentService>(CommitmentService);
            const observationService =
                deps.injector.get<ObservationService>(ObservationService);

            const obses = [] as any as ForkRet;
            if (
                act.payload.which == null ||
                act.payload.which === TabRefId.COMMITMENTS
            ) {
                obses.push(
                    of(null).pipe(
                        commitmentService.listSummary(() => ({
                            userId: act.payload.userId,
                        })),
                    ),
                    of(null).pipe(commitmentService.retrieveCommitmentConfig()),
                );
            } else {
                obses.push(of(null), of(null));
            }
            if (
                act.payload.which == null ||
                act.payload.which === TabRefId.OBSERVATIONS
            ) {
                obses.push(
                    of(null).pipe(
                        observationService.listSummary(
                            () => act.payload.userId,
                        ),
                    ),
                    of(null).pipe(
                        observationService.retrieveObservationConfig(),
                    ),
                );
            } else {
                obses.push(of(null), of(null));
            }

            return of(null).pipe(
                concatMap(() => forkJoin(obses)),
                map((ret) => {
                    return reloadResult(
                        {
                            ...act.payload,
                            commitments: ret[0],
                            observations: ret[2],
                            commitmentConfig: ret[1],
                            observationConfig: ret[3],
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(reloadResult({ ...act.payload }, act.storePath)),
                ),
                takeUntil(
                    action.pipe(
                        ofType(ActionType.COACHING_DETAIL__P2__RELOAD_CANCEL),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath &&
                                (cancelAct.payload.which == null ||
                                    cancelAct.payload.which ===
                                        act.payload.which),
                        ),
                    ),
                ),
            );
        }),
    );

export const coachingDetailPillarTwoEpics = [reload];
